import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { AssetGrid } from '@/components/AssetGrid';
import { Button, CenteredSpinner, EmptyState } from '@/components/ui';
import { api } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useSearch } from '@/lib/queries';

/**
 * Tag review — the human-in-the-loop queue. Shows assets whose AI tags are not
 * yet confirmed, plus batch controls to enqueue untagged assets and drain the
 * tagging queue (parity with Pimcore's BatchProcessImagesCommand + confirm).
 */
export function ReviewPage() {
  const unconfirmed = useSearch({ confirmed: false, perPage: 200 }, true);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function batch(fn: () => Promise<string>) {
    setBusy(true);
    try {
      setStatus(await fn());
      void unconfirmed.refetch();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : t('reviewPage.error'));
    }
    setBusy(false);
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b line surface-card px-5 py-3">
        <div className="mr-auto flex items-center gap-2">
          <Sparkles className="size-4 text-brand-500" />
          <div>
            <h1 className="text-base font-semibold text-strong">{t('reviewPage.title')}</h1>
            <p className="text-xs text-muted">
              {unconfirmed.data ? t('reviewPage.nUnconfirmed', { n: unconfirmed.data.total }) : t('reviewPage.ellipsis')} {status ? `· ${status}` : ''}
            </p>
          </div>
        </div>
        <Button
          variant="secondary"
          className="h-8"
          loading={busy}
          onClick={() => batch(async () => t('reviewPage.nQueued', { n: (await api.enqueueUntagged()).enqueued }))}
        >
          {t('reviewPage.queueUntagged')}
        </Button>
        <Button
          variant="primary"
          className="h-8"
          loading={busy}
          onClick={() => batch(async () => {
            const r = await api.drain(20);
            return t('reviewPage.taggedFailed', { done: r.done, failed: r.failed });
          })}
        >
          {t('reviewPage.processQueue', { n: 20 })}
        </Button>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {unconfirmed.isPending ? (
          <CenteredSpinner />
        ) : (unconfirmed.data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={<Sparkles />} title={t('reviewPage.allConfirmed')} description={t('reviewPage.noPending')} />
        ) : (
          <AssetGrid assets={unconfirmed.data!.items} />
        )}
      </div>
    </div>
  );
}
